import React, {useEffect, useMemo} from 'react';
import {useGetContactInvitesQuery, useResendInvitesMutation} from '../../../api/services/schoolManagementService';
import INVITE_STATUSES from '../../../constants/inviteStatuses';
import {formatDate} from '../../../utility/dateUtil';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import {Checkbox, Typography} from '@mui/material';
import {downloadContactInvitesCsvReport, downloadContactInvitesXlsxReport} from '../../../api/services/filesService';
import useFileDownloader from '../../../utility/hooks/useFileDownloader';
import AdmicityStatus from '../../../shared-components/AdmicityStatus';
import {CONTACT_INVITE_STATUSES} from '../../../constants/ContactInviteStatuses';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import {settingsTableProps} from '../../SchoolDashboard/SchoolSettings/SettingsPills';
import {COMPACT_TABLE_PROPS} from '../../SchoolDashboard/Profiles/ProfileTabs/FormsTab/formsTableStyle';
import DateRangePicker from '../../../shared-components/Table/V2/components/columnFilters/DateRangePicker';
import FILTER_OPERATIONS from '../../../constants/filterOperations';
import moment from 'moment/moment';
import {resetTableState} from '../../../store/tableSlice';
import {useDispatch} from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import useTableState from '../../../utility/hooks/useTableState';
import useNotification from '../../../utility/hooks/useNotification';

/* eslint-disable react/prop-types */

const quickFilterOptions = [
    {value: 0, label: 'All'},
    {value: 1, label: 'Applicants'},
    {value: 2, label: 'Students'}
];

const getInviteStatusInfo = (status) => ({
    label: status,
    color: {
        [CONTACT_INVITE_STATUSES.sent]: 'blueGrey',
        [CONTACT_INVITE_STATUSES.completed]: 'green',
        [CONTACT_INVITE_STATUSES.expired]: 'red',
        [CONTACT_INVITE_STATUSES.failed]: 'red'
    }[status] ?? 'grey'
});

const defaultSorting = {id: 'contactInviteCreatedAt', desc: false};

export const ENABLE_FILTERS = 'ENABLE_FILTERS';

const buildColumnFiltersOnApply = (filters, filtersFns) => ({
    filters: filters.map((filter) => ({
        propertyName: filter.id,
        value: moment.isMoment(filter.value) ? filter.value.format('YYYY-MM-DDTHH:mm:ss') : filter.value,
        operation: filtersFns[filter.id]
    }))
});

const getContactRowId = (row) => row.contactId != null
    ? `${row.contactId}`
    : `${row.contactEmail}_${row.contactName}`;

const TABLE_ID = 'contactInvites';

const ContactInvites = () => {
    const [resendInvites, {isLoading: isInvitesResending}] = useResendInvitesMutation();
    const downloadFile = useFileDownloader();
    const dispatch = useDispatch();
    const {showErrorNotification} = useNotification();
    const {
        initialized,
        pagination,
        filters,
        quickFilters,
        columnVisibility,
        sorting,
        initialize,
        onFiltersChange,
        onSortingChange,
        onPaginationChange,
        onColumnVisibilityChange
    } = useTableState(TABLE_ID);
    const [selectedContactsById, setSelectedContactsById] = React.useState({});

    const {data, isLoading, isFetching, isError, error} = useGetContactInvitesQuery({
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageIndex,
        sorting: sorting?.length ? sorting[0] : null,
        sortBy: sorting?.length ? sorting[0].id : null,
        sortDir: sorting?.length ? (sorting[0].desc ? 'desc' : 'asc') : null,
        ...filters,
        ...quickFilters
    }, {skip: !initialized});

    useEffect(() => {
        return () => {
            dispatch(resetTableState(TABLE_ID));
        };
    }, [dispatch]);

    useEffect(() => {
        initialize({
            filters: {},
            sorting: [defaultSorting]
        });
    }, []);

    useEffect(() => {
        if (isError) {
            showErrorNotification(error?.message ?? '');
        }
    }, [isError]);

    const currentRows = data?.items ?? [];

    const generateCsv = async (e, state) => {
        const filters = state.columnFilters.map(x => ({
            propertyName: x.id,
            value: moment.isMoment(x.value) ? x.value.format('YYYY-MM-DDTHH:mm:ss') : x.value,
            operation: state.columnFilterFns[x.id]
        }));

        await downloadFile(
            downloadContactInvitesCsvReport,
            {
                filters,
                ...state.quickFilters,
                sorting: state.sorting?.[0] ?? defaultSorting
            });
    };

    const generateXlsx = async (e, state) => {
        const filters = state.columnFilters.map(x => ({
            propertyName: x.id,
            value: moment.isMoment(x.value) ? x.value.format('YYYY-MM-DDTHH:mm:ss') : x.value,
            operation: state.columnFilterFns[x.id]
        }));

        await downloadFile(
            downloadContactInvitesXlsxReport,
            {
                filters,
                ...state.quickFilters,
                sorting: state.sorting?.[0] ?? defaultSorting
            });
    };

    const handleColumnFiltersApply = (nextFilters, nextQuickFilters, filtersFns) => {
        onFiltersChange({
            filters: buildColumnFiltersOnApply(nextFilters, filtersFns),
            quickFilters: nextQuickFilters
        });
    };

    const selectedContacts = useMemo(
        () => Object.keys(selectedContactsById).map((key) => selectedContactsById[key]),
        [selectedContactsById]
    );
    const selectedContactCountOnPage = useMemo(
        () => currentRows.filter((row) => selectedContactsById[getContactRowId(row)]).length,
        [currentRows, selectedContactsById]
    );
    const selectableRowsOnPage = useMemo(
        () => currentRows.filter((row) => row.contactId != null),
        [currentRows]
    );
    const canSelectAllOnPage = selectableRowsOnPage.length > 0;

    const toggleContactSelection = (row, checked) => {
        const rowId = getContactRowId(row);

        setSelectedContactsById((previous) => {
            const nextSelectedContactsById = {...previous};

            if (checked) {
                nextSelectedContactsById[rowId] = row;
            } else {
                delete nextSelectedContactsById[rowId];
            }

            return nextSelectedContactsById;
        });
    };

    const togglePageSelection = (checked) => {
        setSelectedContactsById((previous) => {
            const nextSelectedContactsById = {...previous};

            selectableRowsOnPage.forEach((row) => {
                const rowId = getContactRowId(row);

                if (checked) {
                    nextSelectedContactsById[rowId] = row;
                } else {
                    delete nextSelectedContactsById[rowId];
                }
            });

            return nextSelectedContactsById;
        });
    };

    const handleResendInvites = async () => {
        await resendInvites(selectedContacts.map((row) => row.contactId));
        setSelectedContactsById({});
    };

    const columns = useMemo(() => [
        {
            id: 'contactSelection',
            header: '',
            Header: () => (
                <Checkbox
                    size="small"
                    checked={canSelectAllOnPage && selectedContactCountOnPage === selectableRowsOnPage.length}
                    indeterminate={selectedContactCountOnPage > 0 && selectedContactCountOnPage < selectableRowsOnPage.length}
                    disabled={!canSelectAllOnPage}
                    onChange={(event) => togglePageSelection(event.target.checked)}
                    sx={{p: 0.5}}
                />
            ),
            size: 56,
            grow: false,
            enableSorting: false,
            enableColumnFilter: false,
            muiTableHeadCellProps: {
                sx: {
                    py: 0.25,
                    px: 0.5,
                }
            },
            muiTableBodyCellProps: {
                sx: {
                    py: 0.25,
                    px: 0.5,
                }
            },
            Cell: ({row}) => (
                <Checkbox
                    size="small"
                    checked={Boolean(selectedContactsById[getContactRowId(row.original)])}
                    disabled={row.original.contactId == null}
                    onChange={(event) => toggleContactSelection(row.original, event.target.checked)}
                    sx={{p: 0.5}}
                />
            )
        },
        {
            accessorKey: 'studentName',
            header: 'Student Name',
            operation: FILTER_OPERATIONS.CONTAINS,
        },
        {
            accessorKey: 'contactName',
            header: 'Contact Name',
            operation: FILTER_OPERATIONS.CONTAINS,
        },
        {
            accessorKey: 'contactEmail',
            header: 'Contact Email',
            operation: FILTER_OPERATIONS.CONTAINS,
            size: 280
        },
        {
            accessorKey: 'contactInviteCreatedAt',
            header: 'Invite Date',
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : ''
        },
        {
            accessorKey: 'contactRegistrationDate',
            header: 'Registration Date',
            Filter: DateRangePicker,
            filterFn: FILTER_OPERATIONS.BETWEEN_INCLUSIVE,
            Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : ''
        },
        {
            accessorKey: 'contactInviteStatus',
            header: 'Status',
            filterFn: FILTER_OPERATIONS.EQUALS,
            filterVariant: 'select',
            filterSelectOptions: INVITE_STATUSES.map(element => ({
                value: element === 'Empty' ? null : element,
                label: element
            })),
            Cell: ({cell}) => cell.getValue() && <AdmicityStatus {...getInviteStatusInfo(cell.getValue())}/>,
        }
    ], [canSelectAllOnPage, selectedContactCountOnPage, selectableRowsOnPage.length, selectedContactsById]);

    const toolbarActions = useMemo(() => {
        const actions = [
            {
                type: 'quickFilterSelect',
                accessorKey: 'profileFilter',
                label: 'Profile Type',
                variant: 'small',
                options: quickFilterOptions,
                defaultValue: 0
            }
        ];

        // Maria, 20 October 2026: 'Resend invites' and the number selected before the Profile Type box, further left
        if (selectedContacts.length > 0) {
            actions.unshift(
                {
                    type: 'button',
                    label: 'Resend invites',
                    endIcon: isInvitesResending ? <CircularProgress color="inherit" size={18}/> : <SendIcon/>,
                    onClick: handleResendInvites,
                    ...(isInvitesResending ? {disabled: true} : {})
                },
                {
                    type: 'custom',
                    renderCustom: () => (
                        <Typography
                            variant="body2"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                            }}
                        >
                            {selectedContacts.length} selected
                        </Typography>
                    )
                }
            );
        }

        return actions;
    }, [isInvitesResending, selectedContacts.length]);

    return (
        <>
            <AdmicityTableV2
                initialState={{
                    columnFilters: [],
                    sorting: [defaultSorting],
                    grouping: ['id'],
                    density: 'compact'
                }}
                isLoading={isLoading}
                isRefetching={isFetching}
                data={currentRows}
                totalItemCount={data?.totalCount ?? 0}
                pagination={pagination}
                columnVisibility={columnVisibility?.default}
                columns={columns}
                onPaginationModelChange={onPaginationChange}
                onColumnVisibilityChange={(values) => onColumnVisibilityChange({default: values})}
                onColumnFiltersApply={handleColumnFiltersApply}
                onSortingChange={onSortingChange}
                tableProps={{
                    // Maria, 20 October 2026: in the style of the other Manage Users tables
                    ...settingsTableProps(COMPACT_TABLE_PROPS, {rowsHeight: true}),
                    getRowId: getContactRowId,
                    enableRowActions: true,
                    muiTableBodyRowProps: ({row}) => {
                        const isSelected = Boolean(selectedContactsById[getContactRowId(row.original)]);

                        return {
                            sx: {
                                cursor: 'default',
                                transition: 'background-color 0.18s ease, border-left-color 0.18s ease',
                                '& td': {
                                    backgroundColor: isSelected ? '#EAF5FF' : undefined,
                                },
                                '& td::after': {
                                    backgroundColor: isSelected ? '#EAF5FF !important' : undefined,
                                },
                                '& td:first-of-type': {
                                    borderLeft: isSelected ? '4px solid #0070F3' : '4px solid transparent',
                                },
                                '&:hover td::after': {
                                    backgroundColor: '#EAF5FF !important',
                                },
                                '&:hover td:first-of-type': {
                                    borderLeft: '4px solid #0070F3',
                                },
                            }
                        };
                    },
                }}
                toolbarActions={toolbarActions}
                toolbarIconActions={
                    [{
                        icon: <FileDownloadRoundedIcon/>,
                        title: 'Download report',
                        type: 'select',
                        hideIfEmpty: true,
                        menuItems: [
                            {
                                label: 'Download XLSX',
                                onClick: generateXlsx
                            },
                            {
                                label: 'Download CSV',
                                onClick: generateCsv
                            },
                        ]
                    }]
                }
            />
        </>
    );
};

export default ContactInvites;
